import React, { useState } from "react";
import { getFirestore, doc, updateDoc } from "firebase/firestore";
import app from "./firebase";
import emailjs from "emailjs-com";
import { Helmet } from "react-helmet";
import { Leaf, Mail, ArrowLeft, CheckCircle2, AlertCircle, Key } from "lucide-react";

const db = getFirestore(app);

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success' or 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);
    setStatus(null);
    setMessage("");

    try {
      // Basic logic preserved from original: create a random 8-char string and update user doc
      const randomPassword = Math.random().toString(36).slice(-8);
      const userId = email.replace("@", "").replace(".", "").toLowerCase();

      // We update the doc with the new temporary password
      await updateDoc(doc(db, "users", userId), { password: randomPassword });

      // Send email via EmailJS
      const templateParams = { email, link: randomPassword };
      await emailjs.send("service_pehhhv8", "template_pgfyr5n", templateParams, "9o1vflADP-wePeEFd");

      setStatus("success");
      setMessage("Success! A temporary password has been sent to your email.");
      setEmail("");
    } catch (err) {
      console.error(err);
      setStatus("error");
      setMessage("Account not found or system error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "var(--font-sans)", background: "var(--color-white)" }}>
      <Helmet><title>Reset Password — Agrivestia</title></Helmet>

      {/* Brand Panel (Matches Login/Register Style) */}
      <div className="hide-mobile" style={{
        flex: 1, background: "var(--gradient-hero)", display: "flex",
        flexDirection: "column", justifyContent: "center", padding: "80px", position: "relative", overflow: "hidden"
      }}>
        {/* Decorative elements */}
        <div style={{ position: "absolute", top: "-10%", right: "-10%", width: "400px", height: "400px", background: "rgba(255,255,255,0.05)", borderRadius: "50%" }} />
        <div style={{ position: "absolute", bottom: "5%", left: "5%", width: "200px", height: "200px", background: "rgba(255,255,255,0.03)", borderRadius: "50%" }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: "480px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "40px", animation: "fadeInUp 0.6s ease" }}>
            <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Leaf size={24} color="white" />
            </div>
            <span style={{ fontWeight: 800, fontSize: "28px", color: "white", letterSpacing: "-0.03em" }}>AGRIVESTIA</span>
          </div>

          <h2 style={{ fontSize: "40px", fontWeight: 900, color: "white", marginBottom: "24px", lineHeight: 1.1, animation: "fadeInUp 0.8s ease" }}>
            Secure Your <br />
            <span style={{ color: "var(--color-primary-light)" }}>Investment Path</span>
          </h2>
          <p style={{ fontSize: "18px", color: "rgba(255,255,255,0.7)", lineHeight: 1.6, animation: "fadeInUp 1s ease" }}>
            Don't worry, even the best farmers misplace their keys sometimes. We'll help you get back to your dashboard in minutes.
          </p>

          <div style={{ marginTop: "64px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px", animation: "fadeInUp 1.2s ease" }}>
            <div>
              <div style={{ fontSize: "24px", fontWeight: 800, color: "white", marginBottom: "4px" }}>24/7</div>
              <div style={{ fontSize: "14px", color: "rgba(255,255,255,0.5)" }}>Support Availability</div>
            </div>
            <div>
              <div style={{ fontSize: "24px", fontWeight: 800, color: "white", marginBottom: "4px" }}>100%</div>
              <div style={{ fontSize: "14px", color: "rgba(255,255,255,0.5)" }}>Secure Reset Process</div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Panel */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px clamp(20px, 5vw, 80px)" }}>
        <div style={{ width: "100%", maxWidth: "420px" }}>
          <div style={{ marginBottom: "40px", textAlign: "center" }}>
            <div style={{ display: "inline-flex", width: "64px", height: "64px", borderRadius: "16px", background: "var(--color-gray-50)", alignItems: "center", justifyContent: "center", marginBottom: "24px" }}>
              <Key size={32} color="var(--color-primary)" />
            </div>
            <h1 style={{ fontSize: "32px", fontWeight: 900, color: "var(--color-gray-900)", letterSpacing: "-0.02em", marginBottom: "12px" }}>Forgot Password?</h1>
            <p style={{ color: "var(--color-gray-500)", lineHeight: 1.6 }}>
              Enter your registered email and we'll send you a temporary password to regain access.
            </p>
          </div>

          {status && (
            <div style={{
              display: "flex", alignItems: "center", gap: "12px", padding: "16px",
              background: status === "success" ? "var(--color-success-light)" : "var(--color-danger-light)",
              borderRadius: "var(--radius-md)", border: `1px solid ${status === "success" ? "#86efac" : "#fca5a5"}`,
              marginBottom: "24px", animation: "fadeInUp 0.3s ease"
            }}>
              {status === "success" ? <CheckCircle2 size={20} color="#16a34a" /> : <AlertCircle size={20} color="#dc2626" />}
              <p style={{ fontSize: "14px", color: status === "success" ? "#15803d" : "#991b1b", margin: 0, fontWeight: 600 }}>{message}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label className="input-label">Email Address</label>
              <div style={{ position: "relative" }}>
                <Mail size={18} color="var(--color-gray-400)" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                <input
                  type="email"
                  className="input-field"
                  placeholder="name@example.com"
                  style={{ paddingLeft: "44px" }}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary"
              style={{ width: "100%", justifyContent: "center", padding: "14px", marginTop: "8px" }}
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Send Reset Instructions"}
            </button>
          </form>

          <div style={{ marginTop: "32px", textAlign: "center" }}>
            <button
              onClick={() => window.location.href = "/login"}
              style={{ background: "none", border: "none", color: "var(--color-gray-500)", fontSize: "14px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
              onMouseEnter={e => e.target.style.color = "var(--color-primary)"}
              onMouseLeave={e => e.target.style.color = "var(--color-gray-500)"}
            >
              <ArrowLeft size={16} /> Back to Login
            </button>
          </div>

          <div className="show-mobile" style={{ marginTop: "60px", textAlign: "center", display: "none" }}>
            <div style={{ color: "var(--color-gray-300)", fontSize: "12px" }}>
              © {new Date().getFullYear()} Agrivestia Platform
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .hide-mobile { display: none !important; }
          .show-mobile { display: block !important; }
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;
